import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Moon, Play, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import api from '../../lib/api';
import WarmScheduleTable from './WarmScheduleTable';

// Super-admin: nightly health-cache warm-up status + run now.
export default function HealthCacheCard() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get('/health-cache/nightly').then((r) => setD(r.data)).catch(() => setD({ runs: [] })), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (d?.runs?.[0]?.status !== 'running') return undefined; const t = setTimeout(load, 4000); return () => clearTimeout(t); }, [d, load]);

  const runNow = async () => {
    setBusy(true);
    try { await api.post('/health-cache/nightly/run'); toast.success('Warm-up started — rebuilding every organisation\'s health pages'); setTimeout(load, 1500); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not start the warm-up'); }
    finally { setBusy(false); }
  };
  const last = d?.runs?.[0];
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');
  return (
    <Card data-testid="health-cache-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base"><Moon className="h-4 w-4 text-indigo-600" /> Health page cache · nightly warm-up</CardTitle>
        <CardDescription>Transfer, Performance, Story and quarter-delta views are cached for 24h per organisation and rebuilt automatically when data changes. The scheduled job rebuilds each organisation's pages at its own hour ({d?.schedule?.replace('each organisation at its own hour ', '') || 'default 03:00 UTC'}) so the first visit — and the first browser warm-up — each morning is instant. If a run fails or leaves an organisation partially built, every super admin gets an in-app alert.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={runNow} disabled={busy || last?.status === 'running'} className="bg-indigo-600 hover:bg-indigo-700" data-testid="health-cache-warm-now">{busy || last?.status === 'running' ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Play className="h-3.5 w-3.5 mr-1" />} {last?.status === 'running' ? 'Warming…' : 'Warm up now'}</Button>
          <Button size="sm" variant="ghost" onClick={load} data-testid="health-cache-refresh"><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>
          {last && <span className="text-xs text-slate-600" data-testid="health-cache-last-run">Last run {fmt(last.started_at)} · <b className={last.status === 'done' ? 'text-emerald-700' : last.status === 'failed' ? 'text-rose-700' : 'text-indigo-700'}>{last.status}</b> · {last.orgs} orgs · {last.pages} pages · {last.trigger}{last.error ? ` · ${last.error}` : ''}{last.skipped?.length > 0 && <span className="ml-1 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-amber-700" data-testid="health-cache-skipped">{last.skipped.length} org{last.skipped.length !== 1 ? 's' : ''} partially built — super admins notified</span>}</span>}
          {d && !last && <span className="text-xs text-slate-500" data-testid="health-cache-last-run">No warm-up has run yet.</span>}
        </div>
        <WarmScheduleTable />
        {d?.runs?.length > 1 && (
          <ul className="text-[11px] text-slate-500 space-y-0.5" data-testid="health-cache-runs">
            {d.runs.slice(1).map((r) => <li key={r.id}>{fmt(r.started_at)} · {r.status} · {r.orgs} orgs · {r.pages} pages · {r.trigger}</li>)}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
